"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import CommonCalendar from "@/components/utils/calendar/CommonCalendar";
import { CreateWaveDatesConfig } from "@/types/waves.types";
import DateAccordion from "@/components/common/DateAccordion";
import TimePicker from "@/components/common/TimePicker";
import TooltipIconButton from "@/components/common/TooltipIconButton";
import { calculateDecisionTimes, countTotalDecisions, formatDate } from "../services/waveDecisionService";
import { calculateLastDecisionTime } from "@/helpers/waves/create-wave.helpers";
interface RollingEndDateProps {
  readonly dates: CreateWaveDatesConfig;
  readonly setDates: (dates: CreateWaveDatesConfig) => void;
  readonly isRollingMode: boolean;
  readonly setIsRollingMode: (isRolling: boolean) => void;
  readonly isExpanded: boolean;
  readonly setIsExpanded: (expanded: boolean) => void;
}
export default function RollingEndDate(t0) {
  const $ = _c(81);
  const {
    dates,
    setDates,
    isRollingMode,
    isExpanded,
    setIsExpanded
  } = t0;
  const initialDate = dates.endDate ? new Date(dates.endDate) : new Date();
  const [endDateHours, setEndDateHours] = useState(initialDate.getHours());
  const [endDateMinutes, setEndDateMinutes] = useState(initialDate.getMinutes());
  let t1;
  if ($[0] !== dates.firstDecisionTime || $[1] !== dates.subsequentDecisions) {
    t1 = () => {
      if (dates.subsequentDecisions.length === 0) {
        return dates.firstDecisionTime;
      }
      const decisionTimes = calculateDecisionTimes(dates.firstDecisionTime, dates.subsequentDecisions);
      return decisionTimes[decisionTimes.length - 1];
    };
    $[0] = dates.firstDecisionTime;
    $[1] = dates.subsequentDecisions;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const calculateMinEndDate = t1;
  let t2;
  let t3;
  if ($[3] !== dates.endDate) {
    t2 = () => {
      if (dates.endDate) {
        const date = new Date(dates.endDate);
        setEndDateHours(date.getHours());
        setEndDateMinutes(date.getMinutes());
      }
    };
    t3 = [dates.endDate];
    $[3] = dates.endDate;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== dates.endDate) {
    t4 = () => {
      if (!dates.endDate) {
        return null;
      }
      return <div className="tw-flex tw-items-center tw-bg-iron-700/40 tw-px-3 tw-py-2 tw-rounded-lg tw-shadow-md hover:tw-translate-y-[-1px] tw-transition-transform tw-duration-200"><FontAwesomeIcon icon={faCalendarAlt} className="tw-mr-2 tw-size-4 tw-text-primary-400" /><div><p className="tw-mb-0 tw-text-xs tw-text-iron-300/70">Wave End Date</p><p className="tw-mb-0 tw-text-sm tw-font-medium tw-text-iron-50">{formatDate(dates.endDate)}</p></div></div>;
    };
    $[6] = dates.endDate;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const renderCollapsedContent = t4;
  let t5;
  if ($[8] !== dates || $[9] !== endDateHours || $[10] !== endDateMinutes || $[11] !== setDates) {
    t5 = timestamp => {
      const date_0 = new Date(timestamp);
      date_0.setHours(endDateHours);
      date_0.setMinutes(endDateMinutes);
      setDates({
        ...dates,
        endDate: date_0.getTime()
      });
    };
    $[8] = dates;
    $[9] = endDateHours;
    $[10] = endDateMinutes;
    $[11] = setDates;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleDateSelection = t5;
  let t6;
  if ($[13] !== dates || $[14] !== setDates) {
    t6 = (h, m) => {
      setEndDateHours(h);
      setEndDateMinutes(m);
      if (dates.endDate) {
        const date_1 = new Date(dates.endDate);
        date_1.setHours(h);
        date_1.setMinutes(m);
        setDates({
          ...dates,
          endDate: date_1.getTime()
        });
      }
    };
    $[13] = dates;
    $[14] = setDates;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleTimeChange = t6;
  let t7;
  if ($[16] !== dates.endDate || $[17] !== dates.firstDecisionTime || $[18] !== dates.subsequentDecisions) {
    t7 = () => {
      if (!dates.endDate) {
        return 0;
      }
      return countTotalDecisions(dates.firstDecisionTime, dates.subsequentDecisions, dates.endDate);
    };
    $[16] = dates.endDate;
    $[17] = dates.firstDecisionTime;
    $[18] = dates.subsequentDecisions;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const calculateTotalDecisions = t7;
  const t8 = "tw-relative";
  const T0 = DateAccordion;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="tw-flex tw-items-center tw-gap-x-2"><span>Wave End Date</span><TooltipIconButton icon={faInfoCircle} tooltipText="Set when your wave will officially end. With recurring winners, the last announcement may happen before this date." tooltipPosition="bottom" tooltipWidth="tw-w-80" /></div>;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== isExpanded || $[22] !== setIsExpanded) {
    t10 = () => setIsExpanded(!isExpanded);
    $[21] = isExpanded;
    $[22] = setIsExpanded;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== renderCollapsedContent) {
    t11 = renderCollapsedContent();
    $[24] = renderCollapsedContent;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const t12 = "tw-px-5 tw-pt-2 tw-pb-5";
  const t13 = "tw-col-span-2";
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <p className="tw-mb-3 tw-text-base tw-font-medium tw-text-iron-50">Set Wave End Date</p>;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const t15 = "tw-grid tw-grid-cols-1 tw-gap-y-8 tw-gap-x-10 md:tw-grid-cols-2";
  const t16 = "tw-w-full";
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <p className="tw-mb-2 tw-text-sm tw-font-medium tw-text-iron-300">Select Official End Date:</p>;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const T1 = CommonCalendar;
  const t18 = initialDate.getMonth();
  const t19 = initialDate.getFullYear();
  const t20 = dates.endDate ?? initialDate.getTime();
  let t21;
  if ($[28] !== calculateMinEndDate) {
    t21 = calculateMinEndDate();
    $[28] = calculateMinEndDate;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== T1 || $[31] !== handleDateSelection || $[32] !== t18 || $[33] !== t19 || $[34] !== t20 || $[35] !== t21) {
    t22 = <T1 initialMonth={t18} initialYear={t19} selectedTimestamp={t20} minTimestamp={t21} maxTimestamp={null} setSelectedTimestamp={handleDateSelection} />;
    $[30] = T1;
    $[31] = handleDateSelection;
    $[32] = t18;
    $[33] = t19;
    $[34] = t20;
    $[35] = t21;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== t17 || $[38] !== t22) {
    t23 = <div className={t16}>{t17}{t22}</div>;
    $[37] = t17;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <p className="tw-mb-2 tw-text-sm tw-font-medium tw-text-iron-300">Select Time:</p>;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== endDateHours || $[42] !== endDateMinutes || $[43] !== handleTimeChange) {
    t25 = <TimePicker hours={endDateHours} minutes={endDateMinutes} onTimeChange={handleTimeChange} />;
    $[41] = endDateHours;
    $[42] = endDateMinutes;
    $[43] = handleTimeChange;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== dates.endDate || $[46] !== dates.firstDecisionTime || $[47] !== dates.subsequentDecisions || $[48] !== isRollingMode) {
    t26 = dates.endDate && isRollingMode && <div className="tw-mt-4 tw-bg-primary-500/10 tw-rounded-lg tw-p-2"><p className="tw-mb-0 tw-text-xs"><span className="tw-text-iron-300">Last winner announcement will be at:</span></p><p className="tw-mb-0 tw-text-sm tw-text-primary-400 tw-font-medium">{formatDate(calculateLastDecisionTime(dates.firstDecisionTime, dates.subsequentDecisions, dates.endDate))}</p></div>;
    $[45] = dates.endDate;
    $[46] = dates.firstDecisionTime;
    $[47] = dates.subsequentDecisions;
    $[48] = isRollingMode;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t25 || $[51] !== t26) {
    t27 = <div className="tw-w-full">{t24}{t25}{t26}</div>;
    $[50] = t25;
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t23 || $[54] !== t27) {
    t28 = <div className={t15}>{t23}{t27}</div>;
    $[53] = t23;
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== t14 || $[57] !== t28) {
    t29 = <div className={t13}>{t14}{t28}</div>;
    $[56] = t14;
    $[57] = t28;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  const t30 = isRollingMode ? "About Recurring Winners" : "About Wave End Date";
  let t31;
  if ($[59] !== t30) {
    t31 = <p className="tw-mb-1 tw-text-sm tw-font-medium tw-text-iron-200">{t30}</p>;
    $[59] = t30;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  let t32;
  if ($[61] !== isRollingMode) {
    t32 = isRollingMode ? <><p className="tw-text-xs tw-text-iron-400 tw-mb-2">In recurring mode, your wave continues announcing winners in regular intervals until the official end date.</p><p className="tw-text-xs tw-text-iron-400 tw-mb-2">The last winner announcement may occur before the official end date, depending on your cycle timing.</p></> : <p className="tw-text-xs tw-text-iron-400 tw-mb-2">Your wave will end immediately after the final winner announcement.</p>;
    $[61] = isRollingMode;
    $[62] = t32;
  } else {
    t32 = $[62];
  }
  let t33;
  if ($[63] !== calculateTotalDecisions || $[64] !== dates.endDate || $[65] !== isRollingMode) {
    t33 = dates.endDate && isRollingMode && <div className="tw-bg-primary-500/10 tw-rounded-lg tw-p-2 tw-mt-3"><p className="tw-flex tw-items-center tw-justify-between tw-mb-0 tw-text-xs"><span className="tw-text-iron-200">Total winner announcements:</span><span className="tw-text-primary-400 tw-font-semibold">{calculateTotalDecisions()}</span></p></div>;
    $[63] = calculateTotalDecisions;
    $[64] = dates.endDate;
    $[65] = isRollingMode;
    $[66] = t33;
  } else {
    t33 = $[66];
  }
  let t34;
  if ($[67] !== t31 || $[68] !== t32 || $[69] !== t33) {
    t34 = <div className="tw-mt-4 tw-bg-iron-800/30 tw-rounded-lg tw-p-3">{t31}{t32}{t33}</div>;
    $[67] = t31;
    $[68] = t32;
    $[69] = t33;
    $[70] = t34;
  } else {
    t34 = $[70];
  }
  let t35;
  if ($[71] !== t29 || $[72] !== t34) {
    t35 = <div className={t12}>{t29}{t34}</div>;
    $[71] = t29;
    $[72] = t34;
    $[73] = t35;
  } else {
    t35 = $[73];
  }
  let t36;
  if ($[74] !== T0 || $[75] !== isExpanded || $[76] !== t10 || $[77] !== t11 || $[78] !== t35 || $[79] !== t9) {
    t36 = <div className={t8}><T0 title={t9} isExpanded={isExpanded} onToggle={t10} collapsedContent={t11}>{t35}</T0></div>;
    $[74] = T0;
    $[75] = isExpanded;
    $[76] = t10;
    $[77] = t11;
    $[78] = t35;
    $[79] = t9;
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  return t36;
}